import React, { useContext, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  AdminProvider,
  AppSettingsContext,
  DetailToolbar2,
  EvidenceDefaultsProvider,
  ExportProvider,
  InternalSettingsProvider,
  Menubar,
  MenubarContext,
  NamedSettingsProvider,
  ReportingProvider,
  ScheduleProvider,
  SplitScreenMode,
  UserContext,
} from '@eas/common-web';
//import { NotificationButton } from '@components/notification/notification-button';
import logo from './logo.svg';
//import { AppSettings } from './models';
import * as Module from './modules';
import { EvidenceApiUrl, EvidenceBrowserUrl, Permission } from '@enums';
import { useMenuItems } from '@components/menu/menu-items';

import { useExportTag } from './modules/export/export-api';
import { AppSettings } from './models';
import { ProtectedRoute } from '@components/router/protected-route';

export const useStyles = makeStyles({
  logo: {
    maxWidth: '100%',
    width: 'auto',
    height: 'auto',
  },
  title: {
    fontWeight: 400,
    fontSize: 12,
    color: 'white',
    marginLeft: 10,
  },
  userButton: {
    fontSize: 12,
  },
  subMenuItem: {
    margin: 0,
  },
  subMenuItemIcon: {
    minWidth: 25,
  },
});

export function AppSecure() {
  const classes = useStyles();

  const { user, hasPermission, logout } = useContext(UserContext);
  const { pathname } = useLocation();

  const menuRef = useRef<MenubarContext>(null);

  useEffect(() => {
    if (user) {
      const isAdmin = hasPermission(Permission.Default.ADMINISTRATOR);

      // const isOperator = hasPermission(Permission.Default.INTERNAL);

      if (!isAdmin /* && !isOperator */) {
        logout();
      }
    }

    if (!user) {
      window.sessionStorage.setItem('redirectTo', pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ExportTag = useExportTag();
  const { filteredItems, userBtnActions } = useMenuItems(menuRef);

  const { settings } = useContext<AppSettingsContext<AppSettings>>(
    AppSettingsContext as any
  );

  return (
    <>
      {!user ? (
        <Redirect to={'/login'} />
      ) : (
        <ScheduleProvider
          jobUrl="/api/tapis/schedule/jobs"
          runUrl="/api/tapis/schedule/runs"
        >
          {window.sessionStorage.removeItem('redirectTo')}
          <ReportingProvider url="/api/tapis/reporting">
            <ExportProvider url="/api/tapis/export" tags={ExportTag}>
              {/* <UserSettingsProvider url="/api/tapis/user-settings"> */}
              <NamedSettingsProvider
                url="/api/tapis/named-settings"
                defaultTableNamedSettings={false}
              >
                <InternalSettingsProvider
                  url={EvidenceApiUrl.INTERNAL_SETTINGS}
                >
                  <EvidenceDefaultsProvider
                    appName="TAPIS - Portál administrátora"
                    appLogo={logo}
                    enableEmptyDetailContainer={true}
                    ToolbarComponent={DetailToolbar2}
                    splitScreen={
                      +settings.evidenceMode ?? SplitScreenMode.SPLIT
                    }
                  >
                    <Menubar
                      ref={menuRef}
                      userBtnActions={userBtnActions}
                      logoUrl="/"
                      logo={<img src={logo} className={classes.logo} />}
                      title={
                        <Typography className={classes.title}>
                          <FormattedMessage
                            id="TAPIS__APP_TITLE"
                            defaultMessage="TAPIS - Portál administrátora"
                          />
                        </Typography>
                      }
                      items={filteredItems}
                      classOverrides={{
                        userButton: classes.userButton,
                        subMenuItem: classes.subMenuItem,
                        subMenuItemIcon: classes.subMenuItemIcon,
                      }}

                      /*   beforeUserBtn={<NotificationButton />} */
                    >
                      <AdminProvider
                        permission={Permission.Default.ADMINISTRATOR}
                        prefix=""
                        reindexUrl="/api/tapis/reindex"
                      />
                      <Switch>
                        <Route
                          path={EvidenceBrowserUrl.HOME}
                          exact
                          component={Module.TapisDashboard}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.IMPORTS}/:id?`}
                          component={Module.Imports}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.ALOG}/:id?`}
                          component={Module.Alog}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.APP_SETTINGS}/:id?`}
                          component={Module.AppSettings}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.DATA_SOURCE}/:id?`}
                          component={Module.DataSources}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.EXPORT_REQUESTS}/:id?`}
                          component={Module.ExportRequests}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.EXPORT_TEMPLATES}/:id?`}
                          component={Module.ExportTemplates}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={EvidenceBrowserUrl.INTERNAL_SETTINGS}
                          component={Module.InternalSettings}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={EvidenceBrowserUrl.IP_ENTRY}
                          component={Module.IpEntries}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={EvidenceBrowserUrl.MPT}
                          component={Module.Mpt}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={EvidenceBrowserUrl.PARAGRAPH}
                          component={Module.Paragraphs}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.IMPORT_REQUEST}/:id?`}
                          component={Module.ImportRequests}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={EvidenceBrowserUrl.RESEARCH_QUERY}
                          component={Module.ResearchQueries}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.SCHEDULE_JOBS}/:id?`}
                          component={Module.ScheduleJobs}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        {/*     <ProtectedRoute
                          path={`${EvidenceBrowserUrl.SCHEDULE_RUNS}/:id?`}
                          component={Module.ScheduleRuns}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        /> */}
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.SYSTEM_USERS}/:id?`}
                          component={Module.SystemUsers}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.USERS}/:id?`}
                          component={Module.Users}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.NON_STANDARD_STATE}/:id?`}
                          component={Module.NonStandardStates}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                        <ProtectedRoute
                          path={`${EvidenceBrowserUrl.CUSTOM_DEVELOPER_TOOLS}`}
                          component={Module.CustomDeveloperTools}
                          permissions={[Permission.Default.ADMINISTRATOR]}
                        />
                      </Switch>
                    </Menubar>
                  </EvidenceDefaultsProvider>
                </InternalSettingsProvider>
              </NamedSettingsProvider>
              {/*     </UserSettingsProvider> */}
            </ExportProvider>
          </ReportingProvider>
        </ScheduleProvider>
      )}
    </>
  );
}
